import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { PasswordInput } from '@/components/ui/password-input'
import { Label } from '@/components/ui/label'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import AuthLayout from '@/components/AuthLayout'
import { Lock } from 'lucide-react'
import type { CityOption } from '@/types/publicSchool'

const resetPasswordSchema = z
  .object({
    password: z.string().min(8, 'Senha deve ter no mínimo 8 caracteres'),
    password_confirmation: z.string(),
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: 'As senhas não conferem',
    path: ['password_confirmation'],
  })

type ResetPasswordForm = z.infer<typeof resetPasswordSchema>

interface ResetPasswordProps {
  token: string
  /** Alimenta o filtro de cidade do header público, igual às outras páginas. */
  cities: CityOption[]
}

export default function ResetPassword({ token, cities }: ResetPasswordProps) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordForm>({
    resolver: zodResolver(resetPasswordSchema),
  })

  const onSubmit = (data: ResetPasswordForm) => {
    router.patch(`/passwords/${token}`, data)
  }

  return (
    <AuthLayout title="Redefinir senha — BeWave" cities={cities}>
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Lock className="h-6 w-6 text-primary" />
            </div>
            <div>
              <CardTitle className="text-2xl">Nova Senha</CardTitle>
              <CardDescription>Digite sua nova senha</CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Nova Senha</Label>
              <PasswordInput
                id="password"
                placeholder="Mínimo 8 caracteres"
                {...register('password')}
                className={errors.password ? 'border-red-500' : ''}
              />
              {errors.password && (
                <p className="text-sm text-red-500">
                  {errors.password.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password_confirmation">
                Confirmar Nova Senha
              </Label>
              <PasswordInput
                id="password_confirmation"
                placeholder="Digite a senha novamente"
                {...register('password_confirmation')}
                className={errors.password_confirmation ? 'border-red-500' : ''}
              />
              {errors.password_confirmation && (
                <p className="text-sm text-red-500">
                  {errors.password_confirmation.message}
                </p>
              )}
            </div>

            <div className="space-y-2 pt-4">
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? 'Salvando...' : 'Redefinir Senha'}
              </Button>
            </div>
          </form>

          <div className="mt-6 space-y-3">
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                ⚠️ <strong>Segurança:</strong> Escolha uma senha forte com
                letras, números e símbolos.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </AuthLayout>
  )
}
